// Поля форм: FormField (підпис, підказка, помилка, a11y-зв'язки) і обгортки react-hook-form + zod
// (FormInput, FormTextarea, FormSelect, FormCheckbox, FormSwitch, FormDateInput, FormTimeInput, FormCombobox).
// Використання: const form = useForm({ resolver: zodResolver(schema) }); <FormInput control={form.control} name="name" label="Назва" required />.
import { AlertTriangle } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { cn } from '@/lib/cn';
import { Checkbox, Switch } from './Checkbox';
import { Combobox, type ComboboxOption, type ComboboxProps } from './Combobox';
import { DateInput, Input, Select, Textarea, TimeInput, type InputProps, type SelectOption, type TextareaProps } from './Input';

export interface FieldIds {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

export interface FormFieldProps {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  /** id поля; якщо не задано — згенерується і передасться в children(ids). */
  htmlFor?: string;
  children: ReactNode | ((ids: FieldIds) => ReactNode);
  className?: string;
  /** Підпис праворуч від назви (лічильник, «необов'язково»). */
  aside?: ReactNode;
}

/** Обгортка поля: підпис (* — обов'язкове), контрол, підказка або помилка. */
export function FormField({ label, required, hint, error, htmlFor, children, className, aside }: FormFieldProps) {
  const autoId = useId();
  const id = htmlFor ?? autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-error` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] leading-4 font-semibold text-fg-2">
          {label}
          {required ? (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          ) : null}
        </label>
        {aside ? <span className="text-[12px] text-faint">{aside}</span> : null}
      </div>
      {typeof children === 'function' ? children({ id, describedBy, invalid: !!error }) : children}
      {error ? (
        <p id={errId} className="text-[12px] leading-4 text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[12px] leading-4 text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Помилка форми цілком (напр. відповідь сервера). */
export function FormError({ message, className }: { message?: string | null; className?: string }) {
  if (!message) return null;
  return (
    <div role="alert" className={cn('flex items-start gap-2 rounded-field border border-danger-soft bg-danger-soft px-3 py-2 text-[13px] text-danger-ink', className)}>
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p className="whitespace-pre-line">{message}</p>
    </div>
  );
}

interface ControlledBase<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  className?: string;
  disabled?: boolean;
}

type InputOwn = Omit<InputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'defaultValue' | 'disabled'>;

export function FormInput<T extends FieldValues>({ control, name, label, required, hint, className, disabled, ...input }: ControlledBase<T> & InputOwn) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) => (
        <Input
          {...input}
          id={id}
          name={field.name}
          ref={field.ref}
          value={(field.value as string | number | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          invalid={invalid}
          aria-describedby={describedBy}
          aria-required={required || undefined}
        />
      )}
    </FormField>
  );
}

type TextareaOwn = Omit<TextareaProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'defaultValue' | 'disabled'>;

export function FormTextarea<T extends FieldValues>({ control, name, label, required, hint, className, disabled, ...rest }: ControlledBase<T> & TextareaOwn) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) => (
        <Textarea
          {...rest}
          id={id}
          name={field.name}
          ref={field.ref}
          value={(field.value as string | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          invalid={invalid}
          aria-describedby={describedBy}
        />
      )}
    </FormField>
  );
}

export function FormSelect<T extends FieldValues>({
  control,
  name,
  label,
  required,
  hint,
  className,
  disabled,
  options,
  placeholder,
}: ControlledBase<T> & { options: readonly SelectOption[]; placeholder?: string }) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) => (
        <Select
          id={id}
          name={field.name}
          ref={field.ref}
          value={(field.value as string | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          invalid={invalid}
          aria-describedby={describedBy}
          options={options}
          placeholder={placeholder}
        />
      )}
    </FormField>
  );
}

export function FormCheckbox<T extends FieldValues>({ control, name, label, hint, className, disabled }: Omit<ControlledBase<T>, 'required'>) {
  const { field } = useController({ control, name });
  return (
    <Checkbox
      className={className}
      label={label}
      description={hint}
      name={field.name}
      checked={!!field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      disabled={disabled}
    />
  );
}

export function FormSwitch<T extends FieldValues>({ control, name, label, hint, className, disabled }: Omit<ControlledBase<T>, 'required'>) {
  const { field } = useController({ control, name });
  const id = useId();
  return <Switch id={id} className={className} label={label} description={hint} checked={!!field.value} onChange={field.onChange} disabled={disabled} />;
}

export function FormDateInput<T extends FieldValues>({ control, name, label, required, hint, className, disabled }: ControlledBase<T>) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) => (
        <DateInput
          id={id}
          name={field.name}
          ref={field.ref}
          value={(field.value as string | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          invalid={invalid}
          aria-describedby={describedBy}
        />
      )}
    </FormField>
  );
}

export function FormTimeInput<T extends FieldValues>({ control, name, label, required, hint, className, disabled }: ControlledBase<T>) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) => (
        <TimeInput
          id={id}
          name={field.name}
          ref={field.ref}
          value={(field.value as string | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          invalid={invalid}
          aria-describedby={describedBy}
        />
      )}
    </FormField>
  );
}

type ComboboxSource = Pick<
  ComboboxProps,
  'loadOptions' | 'queryKey' | 'options' | 'selectedOptions' | 'resolveOptions' | 'placeholder' | 'minChars' | 'emptyText' | 'clearable'
>;

/** Combobox у формі: значення — id (одиничний) або масив id (multiple). */
export function FormCombobox<T extends FieldValues>({
  control,
  name,
  label,
  required,
  hint,
  className,
  disabled,
  multiple,
  onSelectOption,
  ...source
}: ControlledBase<T> & ComboboxSource & { multiple?: boolean; onSelectOption?: (o: ComboboxOption | ComboboxOption[] | null) => void }) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField label={label} required={required} hint={hint} error={fieldState.error?.message} className={className}>
      {({ id, describedBy, invalid }) =>
        multiple ? (
          <Combobox
            {...source}
            multiple
            id={id}
            name={field.name}
            value={Array.isArray(field.value) ? (field.value as string[]) : []}
            onChange={(v, opts) => {
              field.onChange(v);
              onSelectOption?.(opts);
            }}
            onBlur={field.onBlur}
            disabled={disabled}
            invalid={invalid}
            aria-describedby={describedBy}
          />
        ) : (
          <Combobox
            {...source}
            id={id}
            name={field.name}
            value={(field.value as string | null | undefined) || null}
            onChange={(v, o) => {
              field.onChange(v ?? '');
              onSelectOption?.(o);
            }}
            onBlur={field.onBlur}
            disabled={disabled}
            invalid={invalid}
            aria-describedby={describedBy}
          />
        )
      }
    </FormField>
  );
}
